import { useEffect, useRef, useState, useCallback } from 'react'
import {
  getStripePaymentStatus,
  StripePaymentStatus,
  StripePaymentRecord,
} from '@services/api/billing/payment.api'

const TERMINAL_STATES: StripePaymentStatus[] = [
  'succeeded',
  'failed',
  'refunded',
]
const POLL_INTERVAL_MS = 2000
const TIMEOUT_MS = 60000

export type UsePaymentStatusResult = {
  stripePayment: StripePaymentRecord | null
  status: StripePaymentStatus | null
  isPolling: boolean
  error: string | null
  stopPolling: () => void
}

export const usePaymentStatus = (
  idempotencyKey: string | null,
): UsePaymentStatusResult => {
  const [stripePayment, setStripePayment] =
    useState<StripePaymentRecord | null>(null)
  const [status, setStatus] = useState<StripePaymentStatus | null>(null)
  const [isPolling, setIsPolling] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const activeRef = useRef(false)

  const stopPolling = useCallback((): void => {
    activeRef.current = false
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    setIsPolling(false)
  }, [])

  useEffect(() => {
    if (!idempotencyKey) {
      stopPolling()
      setStripePayment(null)
      setStatus(null)
      setError(null)
      return
    }

    activeRef.current = true
    setIsPolling(true)
    setError(null)

    const poll = async (): Promise<void> => {
      if (!activeRef.current) return
      try {
        const res = await getStripePaymentStatus(idempotencyKey)
        const sp = res.data?.stripe_payment ?? null
        if (!activeRef.current) return
        if (sp) {
          setStripePayment(sp)
          setStatus(sp.status)
          if (TERMINAL_STATES.includes(sp.status)) {
            stopPolling()
          }
        }
      } catch {
        // Silent: keep polling on transient errors; surface after timeout
      }
    }

    poll()
    intervalRef.current = setInterval(poll, POLL_INTERVAL_MS)

    timeoutRef.current = setTimeout(() => {
      if (activeRef.current) {
        setError(
          'Payment confirmation timed out. Please check your payment status.',
        )
        stopPolling()
      }
    }, TIMEOUT_MS)

    return stopPolling
  }, [idempotencyKey, stopPolling])

  return { stripePayment, status, isPolling, error, stopPolling }
}
